import Phaser from 'phaser';
import { sound } from '../../services/audioService.js';
import { ensureTextures } from '../textureGenerator.js';

export class WorkGame extends Phaser.Scene {
  constructor() {
    super({ key: 'WorkGame' });
  }

  init(data) {
    this.difficulty = data.difficulty || 'medium';
    this.barsToAdd = data.barsToAdd || (this.difficulty === 'hard' ? 9 : (this.difficulty === 'medium' ? 6 : 3));
    this.onComplete = data.onComplete;
    this.currentRound = 1;
    this.sequence = [];
    this.playerStep = 0;
    this.isShowingSequence = false;
    this.isGameOver = false;

    if (this.difficulty === 'easy') {
      this.totalRounds = 4;
      this.initialLength = 2;
      this.flashDelay = 450;
    } else if (this.difficulty === 'medium') {
      this.totalRounds = 5;
      this.initialLength = 3;
      this.flashDelay = 380;
    } else {
      this.totalRounds = 6;
      this.initialLength = 4;
      this.flashDelay = 300;
    }
  }

  create() {
    ensureTextures(this);
    const { width, height } = this.cameras.main;

    // Background
    this.add.rectangle(width / 2, height / 2, width, height, 0x181728);

    // Title / HUD
    this.roundText = this.add.text(width / 2, 40, `Round ${this.currentRound} / ${this.totalRounds}`, {
      fontSize: '18px',
      fontStyle: '800',
      color: '#FFD93D'
    }).setOrigin(0.5);

    this.instructionText = this.add.text(width / 2, 68, 'Watch the pattern...', {
      fontSize: '13px',
      color: '#A0A5BA'
    }).setOrigin(0.5);

    // 4 Work Quadrant Pads
    this.pads = [];
    const padData = [
      { id: 0, x: width / 2 - 65, y: height / 2 - 40, col: 0x4D96FF, icon: '💻', name: 'Code' },
      { id: 1, x: width / 2 + 65, y: height / 2 - 40, col: 0x6BCB77, icon: '📊', name: 'Data' },
      { id: 2, x: width / 2 - 65, y: height / 2 + 90, col: 0xFFD93D, icon: '🎨', name: 'Design' },
      { id: 3, x: width / 2 + 65, y: height / 2 + 90, col: 0xFF6B6B, icon: '🚀', name: 'Deploy' }
    ];

    padData.forEach((d) => {
      const container = this.add.container(d.x, d.y);
      const bg = this.add.rectangle(0, 0, 110, 110, d.col, 0.4);
      bg.setStrokeStyle(3, d.col, 0.9);
      bg.setInteractive({ cursor: 'pointer' });

      const icon = this.add.text(0, -12, d.icon, { fontSize: '28px' }).setOrigin(0.5);
      const label = this.add.text(0, 26, d.name, { fontSize: '12px', fontStyle: 'bold', color: '#FFF' }).setOrigin(0.5);

      container.add([bg, icon, label]);
      container.setData('id', d.id);
      container.setData('bg', bg);
      container.setData('color', d.col);

      bg.on('pointerdown', () => {
        if (this.isShowingSequence || this.isGameOver) return;
        this.handlePadPress(d.id);
      });

      this.pads.push(container);
    });

    // Start First Sequence
    this.generateNewSequence();
    this.time.delayedCall(600, () => this.playSequence());
  }

  generateNewSequence() {
    this.sequence = [];
    const len = this.initialLength + (this.currentRound - 1);
    for (let i = 0; i < len; i++) {
      this.sequence.push(Phaser.Math.Between(0, 3));
    }
  }

  playSequence() {
    this.isShowingSequence = true;
    this.playerStep = 0;
    this.instructionText.setText('👀 Watch carefully...');
    this.instructionText.setColor('#FFD93D');

    let idx = 0;
    const playNext = () => {
      if (idx >= this.sequence.length) {
        this.isShowingSequence = false;
        this.instructionText.setText('👉 Repeat the sequence!');
        this.instructionText.setColor('#00E676');
        return;
      }

      const padId = this.sequence[idx];
      this.flashPad(padId);
      idx++;
      this.time.delayedCall(this.flashDelay + 150, playNext);
    };

    playNext();
  }

  flashPad(padId) {
    const pad = this.pads[padId];
    const bg = pad.getData('bg');
    const color = pad.getData('color');

    sound.playTone(300 + padId * 100, 'sine', 0.2, 0.2, 50);

    bg.setFillStyle(color, 1);
    this.tweens.add({
      targets: pad,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 120,
      yoyo: true,
      onComplete: () => {
        bg.setFillStyle(color, 0.4);
      }
    });
  }

  handlePadPress(padId) {
    this.flashPad(padId);

    if (padId === this.sequence[this.playerStep]) {
      this.playerStep++;
      if (this.playerStep >= this.sequence.length) {
        // Round Completed
        sound.playCombo(this.currentRound);
        this.currentRound++;

        if (this.currentRound > this.totalRounds) {
          this.triggerEnd(true);
        } else {
          this.roundText.setText(`Round ${this.currentRound} / ${this.totalRounds}`);
          this.instructionText.setText('🎉 Great! Next sequence...');
          this.generateNewSequence();
          this.time.delayedCall(800, () => this.playSequence());
        }
      }
    } else {
      // Mistake!
      sound.playHit();
      this.cameras.main.shake(200, 0.02);
      this.triggerEnd(false);
    }
  }

  triggerEnd(isWin) {
    this.isGameOver = true;
    const { width, height } = this.cameras.main;
    this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.8);

    if (isWin) {
      sound.playVictory();
      this.add.text(width / 2, height / 2 - 50, '🧠 BRILLIANT FOCUS!', {
        fontSize: '22px',
        fontStyle: '800',
        color: '#00E676'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 10, `+${this.barsToAdd} WORK BARS GAINED! 💼`, {
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#00E676'
      }).setOrigin(0.5);

      const finishBtn = this.add.rectangle(width / 2, height / 2 + 50, 140, 42, 0x00E676)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 50, 'CLAIM & EXIT', {
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#000'
      }).setOrigin(0.5);

      finishBtn.on('pointerdown', () => {
        sound.playClick();
        if (this.onComplete) this.onComplete(true);
      });
    } else {
      sound.playGameOver();
      this.add.text(width / 2, height / 2 - 40, '⚡ PATTERN MISMATCH', {
        fontSize: '20px',
        fontStyle: '800',
        color: '#FF5252'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 5, `Stopped at Round ${this.currentRound}`, {
        fontSize: '13px',
        color: '#A0A5BA'
      }).setOrigin(0.5);

      const retryBtn = this.add.rectangle(width / 2, height / 2 + 45, 120, 38, 0xFFD93D)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 45, 'RETRY', {
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#000'
      }).setOrigin(0.5);

      retryBtn.on('pointerdown', () => {
        sound.playClick();
        this.scene.restart();
      });
    }
  }
}
